<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useCurrency } from 'dashboard/composables/useCurrency';

const props = defineProps({
  data: {
    type: Object,
    default: null,
  },
});

const { t, locale } = useI18n();
const browserLocale = computed(() => locale.value.replace('_', '-'));
const { currency } = useCurrency();

const formatCurrency = value => {
  return new Intl.NumberFormat(browserLocale.value, {
    style: 'currency',
    currency: currency.value,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value || 0);
};

const statusConfig = {
  healthy: {
    color: 'bg-green-500',
    bgColor: 'bg-green-100 dark:bg-green-900/30',
    textColor: 'text-green-700 dark:text-green-400',
    label: 'DEAL_REPORTS.AGING.HEALTHY',
  },
  warning: {
    color: 'bg-yellow-500',
    bgColor: 'bg-yellow-100 dark:bg-yellow-900/30',
    textColor: 'text-yellow-700 dark:text-yellow-400',
    label: 'DEAL_REPORTS.AGING.WARNING',
  },
  danger: {
    color: 'bg-orange-500',
    bgColor: 'bg-orange-100 dark:bg-orange-900/30',
    textColor: 'text-orange-700 dark:text-orange-400',
    label: 'DEAL_REPORTS.AGING.DANGER',
  },
  critical: {
    color: 'bg-red-500',
    bgColor: 'bg-red-100 dark:bg-red-900/30',
    textColor: 'text-red-700 dark:text-red-400',
    label: 'DEAL_REPORTS.AGING.CRITICAL',
  },
};

const summaryItems = computed(() => {
  if (!props.data?.summary) return [];
  return [
    { key: 'healthy', count: props.data.summary.healthy },
    { key: 'warning', count: props.data.summary.warning },
    { key: 'danger', count: props.data.summary.danger },
    { key: 'critical', count: props.data.summary.critical },
  ];
});

const criticalDeals = computed(() => props.data?.critical_deals || []);

const getStatusConfig = status => statusConfig[status] || statusConfig.healthy;
</script>

<template>
  <div
    class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6"
  >
    <div class="flex items-center justify-between mb-6">
      <div>
        <h3 class="text-lg font-semibold text-slate-900 dark:text-white">
          {{ t('DEAL_REPORTS.AGING.TITLE') }}
        </h3>
        <p class="text-sm text-slate-500 dark:text-slate-400">
          {{ t('DEAL_REPORTS.AGING.SUBTITLE') }}
        </p>
      </div>
      <div v-if="data" class="flex items-center gap-2">
        <span
          v-if="data.at_risk_count > 0"
          class="px-2 py-1 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400"
        >
          {{ data.at_risk_count }} {{ t('DEAL_REPORTS.AGING.AT_RISK') }}
        </span>
        <span
          v-else
          class="px-2 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400"
        >
          {{ t('DEAL_REPORTS.AGING.ALL_HEALTHY') }}
        </span>
      </div>
    </div>

    <!-- Content -->
    <div v-if="data" class="space-y-6">
      <!-- Summary Grid -->
      <div class="grid grid-cols-4 gap-3">
        <div
          v-for="item in summaryItems"
          :key="item.key"
          class="p-3 rounded-lg text-center"
          :class="getStatusConfig(item.key).bgColor"
        >
          <p
            class="text-2xl font-bold"
            :class="getStatusConfig(item.key).textColor"
          >
            {{ item.count }}
          </p>
          <p class="text-xs" :class="getStatusConfig(item.key).textColor">
            {{ t(getStatusConfig(item.key).label) }}
          </p>
        </div>
      </div>

      <!-- Critical Deals List -->
      <div v-if="criticalDeals.length > 0" class="space-y-3">
        <h4 class="text-sm font-medium text-slate-700 dark:text-slate-300">
          {{ t('DEAL_REPORTS.AGING.CRITICAL_DEALS') }}
        </h4>
        <div class="space-y-2 max-h-48 overflow-y-auto">
          <div
            v-for="deal in criticalDeals"
            :key="deal.id"
            class="flex items-center justify-between p-3 rounded-lg border border-slate-200 dark:border-slate-700"
            :class="[getStatusConfig(deal.status).bgColor]"
          >
            <div class="flex items-center gap-3">
              <div
                class="w-2 h-8 rounded-full"
                :class="getStatusConfig(deal.status).color"
              />
              <div>
                <p class="text-sm font-medium text-slate-900 dark:text-white">
                  {{ deal.contact_name || 'Unknown Contact' }}
                </p>
                <p class="text-xs text-slate-500 dark:text-slate-400">
                  {{ deal.stage_label }} - {{ deal.days_in_stage }}d /
                  {{ deal.max_days }}d
                </p>
              </div>
            </div>
            <div class="text-right">
              <p class="text-sm font-medium text-slate-900 dark:text-white">
                {{ formatCurrency(deal.deal_value) }}
              </p>
              <p
                class="text-xs font-medium"
                :class="getStatusConfig(deal.status).textColor"
              >
                {{ deal.age_percentage }}% {{ t('DEAL_REPORTS.AGING.AGE') }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty critical deals -->
      <div v-else class="flex items-center justify-center py-8 text-center">
        <div>
          <span class="i-lucide-check-circle text-4xl text-green-500 mb-2" />
          <p class="text-slate-500 dark:text-slate-400">
            {{ t('DEAL_REPORTS.AGING.NO_CRITICAL') }}
          </p>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="flex items-center justify-center h-48">
      <p class="text-slate-500 dark:text-slate-400">
        {{ t('DEAL_REPORTS.NO_DATA') }}
      </p>
    </div>
  </div>
</template>
